// Open a tour — the detail view. It owns the DATA and HANDLERS and composes the
// CHILD components (<TourDetailGrid>, <AddArtworksPanel>) against their prop
// interfaces.
//
// This view owns the DATA and HANDLERS:
//   - finds the selected TourGroup and its ordered `slides` (photos + quote slides);
//   - computes `candidates` (artifacts not in the tour) for the add picker;
//   - renders the header (title + counts + Back + New quote slide + Add artworks);
//   - composes <TourDetailGrid> (rearrange + remove, mixed slide types) and an
//     "Add artworks" affordance that toggles <AddArtworksPanel>;
//   - wires onReorder -> reorder, onRemove -> (removeFromTour | removeQuoteSlide by
//     slide kind), onAdd -> addToTour, and "New quote slide" -> onAddQuoteSlide.

import { useMemo, useState } from "react";
import type { Artifact } from "../types";
import type { ExportState } from "../useTours";
import {
  artifactsNotInTour,
  slideCounts,
  tourTitle,
  type TourGroup,
  type TourSlide,
} from "../tours";
import { TourDetailGrid } from "../components/TourDetailGrid";
import { AddArtworksPanel } from "../components/AddArtworksPanel";
import "./TourDetailView.css";

interface TourDetailViewProps {
  slug: string;
  /** All derived tours; this view picks the one matching `slug`. */
  groups: TourGroup[];
  /** Every artifact, for computing the add-picker candidates. */
  allArtifacts: Artifact[];
  loading: boolean;
  onBack: () => void;
  /** Open the QuoteSlideBuilder preselected to this tour. */
  onAddQuoteSlide: () => void;
  /** Persist a new display order for the tour (durable: PUT /tours/{slug} {order}). */
  reorder: (slug: string, orderedIds: string[]) => void;
  /** Add an artifact to the tour (durable: adds the slug to its tourTags). */
  addToTour: (artifactId: string, slug: string) => Promise<void>;
  /** Remove an artifact from the tour (durable: drops the slug; keeps the artifact). */
  removeFromTour: (artifactId: string, slug: string) => Promise<void>;
  /** Remove a quote slide from the tour (durable: drops it from quoteSlides + order). */
  removeQuoteSlide: (slug: string, id: string) => Promise<void>;
  /** (Re)build the tour's offline HTML on the server, then download it. */
  exportTour: (slug: string) => Promise<void>;
  /** Current export status/message/url for this slug. */
  exportState: (slug: string) => ExportState;
}

export function TourDetailView({
  slug,
  groups,
  allArtifacts,
  loading,
  onBack,
  onAddQuoteSlide,
  reorder,
  addToTour,
  removeFromTour,
  removeQuoteSlide,
  exportTour,
  exportState,
}: TourDetailViewProps) {
  // Whether the "Add artworks" panel is open.
  const [adding, setAdding] = useState(false);
  // True while a membership mutation (add/remove) is in flight, so the children
  // can disable their controls. Order changes are local/synchronous, not "busy".
  const [busy, setBusy] = useState(false);

  const group = useMemo(
    () => groups.find((g) => g.slug === slug),
    [groups, slug],
  );
  const slides = group?.slides ?? [];
  const candidates = useMemo(
    () => artifactsNotInTour(allArtifacts, slug),
    [allArtifacts, slug],
  );

  // --- Handlers wired down to the child components -------------------------

  const handleReorder = (orderedIds: string[]) => {
    // Optimistic + durable: the store updates the order instantly and persists it
    // to DynamoDB (PUT /tours/{slug}) in the background, so this stays synchronous.
    reorder(slug, orderedIds);
  };

  const handleRemove = async (slide: TourSlide) => {
    setBusy(true);
    try {
      if (slide.kind === "photo") {
        await removeFromTour(slide.artifact.id, slug);
      } else {
        await removeQuoteSlide(slug, slide.id);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (artifactId: string) => {
    setBusy(true);
    try {
      await addToTour(artifactId, slug);
    } finally {
      setBusy(false);
    }
  };

  // "Download for iPad": the store flips per-slug status and (on success) triggers
  // the same-origin attachment download. We just kick it off and read the state.
  const xp = exportState(slug);
  const exporting = xp.status === "generating";
  const handleExport = () => {
    if (exporting) return;
    void exportTour(slug);
  };

  const { photos, quotes } = slideCounts(slides);

  return (
    <div className="tour-detail">
      <header className="tour-detail__header">
        <button type="button" className="btn btn--small" onClick={onBack}>
          ← Back
        </button>
        <div className="tour-detail__heading">
          <h1 className="tour-detail__title">{tourTitle(slug)}</h1>
          <span className="tour-detail__count">
            {photos} {photos === 1 ? "artwork" : "artworks"}
            {quotes > 0 && (
              <> · {quotes} quote {quotes === 1 ? "slide" : "slides"}</>
            )}
          </span>
        </div>
        <div className="tour-detail__actions">
          <button
            type="button"
            className="btn btn--small tour-detail__download-btn"
            onClick={handleExport}
            disabled={exporting}
            aria-busy={exporting}
            title="Build one offline file and save it to this iPad"
          >
            {exporting ? (
              <>
                <span className="tour-detail__spinner" aria-hidden="true" />
                Generating…
              </>
            ) : (
              "⬇ Download for iPad"
            )}
          </button>
          <button
            type="button"
            className="btn btn--small"
            onClick={onAddQuoteSlide}
          >
            ✎ New quote slide
          </button>
          <button
            type="button"
            className="btn btn--small btn--primary"
            onClick={() => setAdding((v) => !v)}
          >
            {adding ? "Done adding" : "Add artworks"}
          </button>
        </div>
      </header>

      {/* Friendly, non-technical guidance + live status for the offline export. */}
      <p
        className={`tour-detail__export-note${
          xp.status === "error" ? " tour-detail__export-note--error" : ""
        }`}
        aria-live="polite"
      >
        {xp.status === "error"
          ? `Couldn't build the file: ${xp.message ?? "please try again."}`
          : xp.status === "done"
            ? `✓ Downloaded. ${xp.message ?? ""} Re-download after you rearrange to refresh it.`
            : "Builds one offline file (no Wi-Fi needed). Saves to Files — open it there or AirDrop it. Re-download after you rearrange to refresh it."}
      </p>

      {slides.length > 1 && (
        <p className="tour-detail__hint">
          Drag the <span className="tdg-grip" aria-hidden="true">⠿</span> grip to
          reorder, or use the <strong>Top / Up / Down</strong> buttons. Quote slides
          and artworks share one sequence. Changes save automatically.
        </p>
      )}

      {/* A tour with no members can still exist transiently (e.g. its last item
          was just removed) — let the grid show its own empty state. We only warn
          loudly if the slug isn't found at all AND we're done loading. */}
      {!group && !loading && (
        <div className="empty-state">
          This tour has no slides. Add artworks or a quote slide, or pick another tour.
        </div>
      )}

      {/* CHILD: rearrange + remove a mixed list of photo and quote slides. */}
      <TourDetailGrid
        slides={slides}
        onReorder={handleReorder}
        onRemove={handleRemove}
        busy={busy}
      />

      {/* "Add artworks" affordance -> CHILD: the picker. */}
      {adding && (
        <AddArtworksPanel
          slug={slug}
          candidates={candidates}
          onAdd={handleAdd}
          onClose={() => setAdding(false)}
          busy={busy}
        />
      )}
    </div>
  );
}
